// @mui
import { alpha, styled } from '@mui/material/styles';
import {
    ListItemText,
    ListItemIcon,
    ListSubheader,
    ListItemButton,
    ListItemTextProps,
    ListItemButtonProps,
    ListSubheaderProps,
} from '@mui/material';
// config
import { ICON, NAVBAR } from '../../../config';

// ----------------------------------------------------------------------

export const navSubheaderLabelClass = 'nav-subheader-label';

// ----------------------------------------------------------------------

export interface ListItemStyleProps extends ListItemButtonProps {
    active: boolean;
    depth: number;
}

export const ListItemStyle = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== 'active',
})<ListItemStyleProps>(({ active, depth, theme }) => ({
    position: 'relative',
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    borderRadius: theme.shape.borderRadius,
    height: NAVBAR.MAIN_ITEM_ROOT_HEIGHT,
    // Active item
    ...(active && {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    }),
    // Active item
    ...(active &&
        depth !== 1 && {
            color: theme.palette.text.primary,
            backgroundColor: 'transparent',
        }),
    // Sub item
    ...(depth && {
        ...(depth > 1 && {
            height: NAVBAR.MAIN_ITEM_SUB_HEIGHT,
        }),
        ...(depth > 2 && {
            paddingLeft: theme.spacing(depth),
        }),
    }),
}));

// ----------------------------------------------------------------------

export const ListItemTextStyle = styled(ListItemText)<ListItemTextProps>(({ theme }) => ({
    transition: theme.transitions.create(['width', 'opacity'], {
        duration: theme.transitions.duration.shorter,
    }),
}));

// ----------------------------------------------------------------------

export const ListItemIconStyle = styled(ListItemIcon)({
    width: ICON.NAVBAR_ITEM,
    height: ICON.NAVBAR_ITEM,
});

// ----------------------------------------------------------------------

export interface ListSubheaderStyleProps extends ListSubheaderProps {
    stuck: boolean;
    isCollapse: boolean;
}

export const ListSubheaderStyle = styled(ListSubheader, {
    shouldForwardProp: (prop) => prop !== 'stuck' && prop !== 'isCollapse',
})<ListSubheaderStyleProps>(({ stuck, isCollapse, theme }) => ({
    ...theme.typography.overline,
    top: -1,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    transition: theme.transitions.create(['opacity', 'height', 'padding', 'background-color'], {
        duration: theme.transitions.duration.shorter,
    }),
    // Collapsed rail has no room for a label, so the row gives its space back
    ...(isCollapse && {
        opacity: 0,
        height: theme.spacing(1),
        paddingTop: 0,
        paddingBottom: 0,
    }),
    [`& .${navSubheaderLabelClass}`]: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        borderRadius: 11,
        transition: theme.transitions.create(
            ['background-color', 'color', 'padding', 'box-shadow'],
            { duration: theme.transitions.duration.shorter }
        ),
        // Pinned to the top of the scroll area, overlapping the nav items beneath it
        ...(stuck && {
            marginLeft: theme.spacing(-1.25),
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.text.secondary, 0.16),
            boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.text.secondary, 0.12)}`,
            backdropFilter: 'blur(6px)',
        }),
    },
}));
